import { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileQuestion,
  Clock,
  Trophy,
  Lock,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Star,
  AlertTriangle,
  Loader2,
  BookOpen,
  Timer,
  Flame,
  Target,
  Medal,
  X
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BeltIndicator } from '../shared/BeltBadge';
import { useBelts } from '../../contexts/BeltContext';
import BadgeCard from '../shared/BadgeCard';
import BadgeDetailModal from '../shared/BadgeDetailModal';

import { CDN_URL } from '@/config/cdn';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Sound effect helpers
const playGongSound = () => {
  try {
    const audio = new Audio(`${CDN_URL}/audio/sfx/gong.mp3`);
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

const playPageTurnSound = () => {
  try {
    // Using streak-best at low volume for a soft whoosh effect
    const audio = new Audio(`${CDN_URL}/audio/sfx/streak-best.mp3`);
    audio.volume = 0.12;
    audio.playbackRate = 2.0; // Speed up for quick swipe feel
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

// Shuffle array using Fisher-Yates algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Process questions: shuffle options and add "None of the Above" to ~35% spread out
const processQuestionsForQuiz = (questions) => {
  // First, identify which multiple choice questions could get NOTA
  const mcQuestions = questions.map((q, idx) => ({ q, idx }))
    .filter(({ q }) => q.question_type === 'multiple_choice');

  // Calculate how many should get NOTA (35%)
  const notaCount = Math.round(mcQuestions.length * 0.35);

  // Spread them out by selecting indices evenly
  const notaIndices = new Set();
  if (notaCount > 0 && mcQuestions.length > 0) {
    const spacing = mcQuestions.length / notaCount;
    for (let i = 0; i < notaCount; i++) {
      // Add some randomness within a range to avoid perfect pattern
      const baseIdx = Math.floor(i * spacing);
      const randomOffset = Math.floor(Math.random() * Math.max(1, Math.floor(spacing * 0.5)));
      const actualIdx = Math.min(baseIdx + randomOffset, mcQuestions.length - 1);
      notaIndices.add(mcQuestions[actualIdx].idx);
    }
  }

  return questions.map((question, idx) => {
    if (question.question_type !== 'multiple_choice' || !question.options) {
      return question;
    }

    // Check if any option is already "None of the Above"
    const hasNota = question.options.some(opt =>
      opt.text.toLowerCase().includes('none of the above') ||
      opt.text.toLowerCase().includes('none of these')
    );

    let options = [...question.options];

    // Add NOTA if this question was selected and doesn't already have it
    if (notaIndices.has(idx) && !hasNota) {
      // Only add NOTA if the correct answer is one of the existing options
      const hasCorrectAnswer = options.some(opt => opt.is_correct);
      if (hasCorrectAnswer) {
        options.push({
          id: `nota-${question.id}`,
          text: 'None of the above',
          is_correct: false
        });
      }
    }

    // Shuffle the options
    const shuffledOptions = shuffleArray(options);

    return {
      ...question,
      options: shuffledOptions
    };
  });
};

// ============================================
// Quiz Result Screen
// ============================================
const QuizResult = ({ result, quiz, onRetry, onBack }) => {
  const passed = result.passed;
  const percentage = result.score_percentage;
  const audioRef = useRef(null);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const playAudio = useCallback((audioUrl) => {
    if (audioRef.current && audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.play().catch(err => console.error('Audio playback failed:', err));
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Hidden audio element for badge pronunciation */}
      <audio ref={audioRef} />

      {/* Badge Detail Modal */}
      {selectedBadge && (
        <BadgeDetailModal
          badge={selectedBadge}
          onClose={() => setSelectedBadge(null)}
          onPlayAudio={playAudio}
        />
      )}

      <Card className={passed ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/30' : 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/30'}>
        <CardContent className="p-8 text-center">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
            passed ? 'bg-green-100 dark:bg-green-800' : 'bg-orange-100 dark:bg-orange-800'
          }`}>
            {passed ? (
              <Trophy className="h-10 w-10 text-green-600" />
            ) : (
              <AlertTriangle className="h-10 w-10 text-orange-600" />
            )}
          </div>

          <h2 className="text-2xl font-bold mb-2">
            {passed ? 'Congratulations!' : 'Keep Practicing!'}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {passed
              ? 'You passed the quiz!'
              : `You need ${quiz.passing_score}% to pass.`}
          </p>

          <div className="text-5xl font-bold mb-2">
            {percentage.toFixed(0)}%
          </div>

          <div className="flex justify-center gap-6 text-sm text-gray-600 dark:text-gray-400 mb-6">
            <div>
              <span className="font-semibold text-green-600">{result.correct_count}</span> correct
            </div>
            <div>
              <span className="font-semibold text-red-600">{result.incorrect_count}</span> incorrect
            </div>
            <div>
              <span className="font-semibold text-yellow-600">+{result.total_points}</span> points
            </div>
          </div>

          {result.new_badges?.length > 0 && (
            <div className="bg-gradient-to-br from-purple-50 to-indigo-100 dark:from-purple-900/40 dark:to-indigo-900/40 rounded-xl p-6 mb-6 border border-purple-200 dark:border-purple-700">
              <p className="text-purple-800 dark:text-purple-300 font-bold mb-4 flex items-center justify-center gap-2">
                <Medal className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                New Badges Earned!
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                {result.new_badges.map((badge, i) => (
                  <BadgeCard
                    key={badge.id || i}
                    badge={{ ...badge, earned: true }}
                    size="md"
                    showProgress={false}
                    isNew={true}
                    onClick={() => setSelectedBadge({ ...badge, earned: true })}
                    onPlayAudio={playAudio}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onBack}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Quizzes
            </Button>
            {quiz.allow_retake && !passed && (
              <Button onClick={onRetry} variant="memberPrimary">
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Answer Review (if enabled) */}
      {quiz.show_answers_after && result.answers && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Review Your Answers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {result.answers.map((answer, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${
                  answer.is_correct ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/30' : 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  {answer.is_correct ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium mb-2">{answer.question_text}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Your answer: <span className="font-medium">{answer.student_answer || '(no answer)'}</span>
                    </p>
                    {!answer.is_correct && answer.correct_answer && (
                      <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                        Correct answer: <span className="font-medium">{answer.correct_answer}</span>
                      </p>
                    )}
                    {answer.explanation && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 italic">{answer.explanation}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================
// Quiz Player Component
// ============================================
const QuizPlayer = ({ quiz, studentId, token, onComplete, onBack }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [answeredCorrectly, setAnsweredCorrectly] = useState({}); // Track correctness for immediate feedback
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [timerPulse, setTimerPulse] = useState(false); // For gong animation

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Start quiz and fetch questions
  useEffect(() => {
    const startQuiz = async () => {
      try {
        // Start the quiz attempt
        const startRes = await fetch(
          `${API_BASE}/api/customer/students/${studentId}/quizzes/${quiz.id}/start`,
          {
            method: 'POST',
            headers: getAuthHeaders()
          }
        );

        if (!startRes.ok) {
          const error = await startRes.json();
          alert(error.detail || 'Failed to start quiz');
          onBack();
          return;
        }

        const startData = await startRes.json();
        setAttemptId(startData.attempt_id);

        // Play gong sound on quiz start with timer pulse
        playGongSound();
        setTimerPulse(true);
        setTimeout(() => setTimerPulse(false), 1000);

        // Fetch questions
        const questionsRes = await fetch(
          `${API_BASE}/api/customer/students/${studentId}/quizzes/${quiz.id}`,
          { headers: getAuthHeaders() }
        );

        if (questionsRes.ok) {
          const data = await questionsRes.json();
          // Shuffle question order, then process: shuffle options and add "None of the Above" to some
          const shuffledQuestions = shuffleArray(data.questions || []);
          const processedQuestions = processQuestionsForQuiz(shuffledQuestions);
          setQuestions(processedQuestions);
          if (quiz.time_limit_minutes > 0) {
            setTimeRemaining(quiz.time_limit_minutes * 60);
          }
        }
      } catch (error) {
        console.error('Failed to start quiz:', error);
        alert('Failed to start quiz');
        onBack();
      } finally {
        setLoading(false);
      }
    };

    startQuiz();
  }, [quiz.id, studentId, getAuthHeaders, onBack, quiz.time_limit_minutes]);

  // Timer countdown
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // Time's up - auto submit
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentIndex];

  const handleAnswer = (value) => {
    // For multiple choice, don't allow changing after selection (for immediate feedback)
    // But for short answer/lineage, always allow typing
    if (currentQuestion.question_type === 'multiple_choice' && answers[currentQuestion.id] !== undefined) {
      return;
    }

    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));

    // For multiple choice, check correctness immediately
    if (currentQuestion.question_type === 'multiple_choice') {
      const correctOption = currentQuestion.options?.find(opt => opt.is_correct);
      const isCorrect = correctOption && value === correctOption.id;
      setAnsweredCorrectly(prev => ({
        ...prev,
        [currentQuestion.id]: isCorrect
      }));
    }
  };

  const goNext = () => {
    if (currentIndex < questions.length - 1) {
      playPageTurnSound();
      setCurrentIndex(currentIndex + 1);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      playPageTurnSound();
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const unanswered = questions.filter(q => !answers[q.id]).length;
    if (unanswered > 0 && timeRemaining !== 0) {
      if (!window.confirm(`You have ${unanswered} unanswered question(s). Submit anyway?`)) {
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/quizzes/${quiz.id}/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            attempt_id: attemptId,
            responses: Object.entries(answers).map(([questionId, answer]) => ({
              question_id: questionId,
              answer: answer
            }))
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        onComplete(result);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to submit quiz');
      }
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      alert('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FileQuestion className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">This quiz has no questions.</p>
          <Button onClick={onBack} className="mt-4">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Quizzes
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Quiz Title and Timer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Exit
          </Button>
          <h2 className="font-semibold text-lg text-gray-900 dark:text-gray-50 hidden sm:block">{quiz.title}</h2>
        </div>

        {timeRemaining !== null && (
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg font-mono transition-all ${
            timeRemaining <= 60 ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300' : 'bg-gray-100 dark:bg-gray-800'
          } ${timerPulse ? 'animate-pulse scale-110 ring-2 ring-yellow-400' : ''}`}>
            <Timer className="h-4 w-4" />
            {formatTime(timeRemaining)}
          </div>
        )}
      </div>

      {/* Quiz title on mobile */}
      <h2 className="font-semibold text-lg text-gray-900 dark:text-gray-50 text-center sm:hidden">{quiz.title}</h2>

      {/* Progress */}
      <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className="bg-blue-600 rounded-full h-2 transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
        />
      </div>
      <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
        <span>Question {currentIndex + 1} of {questions.length}</span>
        <span>{Object.keys(answers).length} answered</span>
      </div>

      {/* Question Card */}
      <Card>
        <CardContent className="p-6">
          <div className="mb-4">
            <span className={`text-xs px-2 py-1 rounded-full ${
              currentQuestion.question_type === 'multiple_choice'
                ? 'bg-blue-100 text-blue-700'
                : currentQuestion.question_type === 'short_answer'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {currentQuestion.question_type === 'multiple_choice' && 'Multiple Choice'}
              {currentQuestion.question_type === 'short_answer' && 'Short Answer'}
              {currentQuestion.question_type === 'lineage' && 'Lineage'}
            </span>
            <span className="text-xs text-gray-400 ml-2">
              {currentQuestion.points} pts
            </span>
          </div>

          <p className="text-lg font-medium mb-6">{currentQuestion.text}</p>

          {/* Multiple Choice with immediate feedback */}
          {currentQuestion.question_type === 'multiple_choice' && (
            <div className="space-y-2">
              {currentQuestion.options?.map((option) => {
                const isSelected = answers[currentQuestion.id] === option.id;
                const hasAnswered = answers[currentQuestion.id] !== undefined;
                const showCorrect = hasAnswered && option.is_correct;
                const showIncorrect = hasAnswered && isSelected && !option.is_correct;

                return (
                  <button
                    key={option.id}
                    onClick={() => handleAnswer(option.id)}
                    disabled={hasAnswered}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      showCorrect
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                        : showIncorrect
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/30'
                        : isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : hasAnswered
                        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-60'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{option.text}</span>
                      {showCorrect && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />}
                      {showIncorrect && <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Short Answer / Lineage */}
          {(currentQuestion.question_type === 'short_answer' ||
            currentQuestion.question_type === 'lineage') && (
            <>
              {currentQuestion.resource_id && currentQuestion.resource && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Reference Resource:</p>
                  <a
                    href={currentQuestion.resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:underline"
                  >
                    <BookOpen className="h-4 w-4" />
                    {currentQuestion.resource.title}
                  </a>
                </div>
              )}
              <textarea
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswer(e.target.value)}
                className="w-full p-4 border rounded-lg min-h-[120px]"
                placeholder="Type your answer here..."
              />
              {currentQuestion.question_type === 'short_answer' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Short answer questions will be reviewed by an instructor
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={goPrev}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>

        {currentIndex === questions.length - 1 ? (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="memberPrimary"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            Submit Quiz
          </Button>
        ) : (
          <Button onClick={goNext}>
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Question Status Indicators (non-clickable) */}
      <div className="flex flex-wrap gap-2 justify-center pt-4 border-t">
        {questions.map((q, i) => {
          const hasAnswer = answers[q.id] !== undefined;
          const isCorrect = answeredCorrectly[q.id] === true;
          const isIncorrect = hasAnswer && answeredCorrectly[q.id] === false;
          const isCurrent = i === currentIndex;

          return (
            <div
              key={q.id}
              className={`w-8 h-8 rounded-full text-sm font-medium flex items-center justify-center transition-all ${
                isCurrent ? 'ring-2 ring-offset-2 ring-blue-400' : ''
              } ${
                isCorrect
                  ? 'bg-green-500 text-white'
                  : isIncorrect
                  ? 'bg-red-500 text-white'
                  : hasAnswer
                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
            >
              {i + 1}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// Quiz Materials Modal
// ============================================
const QuizMaterialsModal = ({ quiz, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              Study Materials - {quiz.title}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {quiz.sources_html ? (
            <div
              className="prose max-w-none quiz-materials-content"
              dangerouslySetInnerHTML={{ __html: quiz.sources_html }}
            />
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No study materials available for this quiz.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Styling for rendered HTML */}
      <style jsx global>{`
        .quiz-materials-content {
          font-size: 0.95rem;
          line-height: 1.6;
        }
        .quiz-materials-content p {
          margin: 0.75rem 0;
        }
        .quiz-materials-content h1 {
          font-size: 1.75rem;
          font-weight: bold;
          margin: 1.25rem 0 0.75rem 0;
          color: #1f2937;
        }
        .quiz-materials-content h2 {
          font-size: 1.4rem;
          font-weight: bold;
          margin: 1rem 0 0.5rem 0;
          color: #374151;
        }
        .quiz-materials-content h3 {
          font-size: 1.15rem;
          font-weight: 600;
          margin: 0.75rem 0 0.5rem 0;
          color: #4b5563;
        }
        .quiz-materials-content ul, .quiz-materials-content ol {
          padding-left: 1.5rem;
          margin: 0.75rem 0;
        }
        .quiz-materials-content li {
          margin: 0.4rem 0;
        }
        .quiz-materials-content a {
          color: #2563eb;
          text-decoration: underline;
          transition: color 0.2s;
        }
        .quiz-materials-content a:hover {
          color: #1d4ed8;
        }
        .quiz-materials-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .quiz-materials-content strong {
          font-weight: 600;
          color: #111827;
        }
        .quiz-materials-content em {
          font-style: italic;
        }
        .quiz-materials-content code {
          background: #f3f4f6;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: 'Courier New', monospace;
          font-size: 0.9em;
        }
      `}</style>
    </div>
  );
};

// ============================================
// Quiz List Component
// ============================================
const QuizList = ({ quizzes, onSelectQuiz }) => {
  const [materialsQuiz, setMaterialsQuiz] = useState(null);

  return (
    <>
      {/* Materials Modal */}
      {materialsQuiz && (
        <QuizMaterialsModal
          quiz={materialsQuiz}
          onClose={() => setMaterialsQuiz(null)}
        />
      )}

      <div className="grid gap-4">
        {quizzes.map(quiz => (
          <Card
          key={quiz.id}
          className={`hover:shadow-md transition-all cursor-pointer ${
            !quiz.is_accessible ? 'opacity-60' : ''
          }`}
          onClick={() => quiz.is_accessible && onSelectQuiz(quiz)}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-lg">{quiz.title}</h3>
                  {quiz.is_featured && (
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                  )}
                  {quiz.passed && (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  )}
                </div>

                {quiz.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                    {quiz.description}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <FileQuestion className="h-4 w-4" />
                    {quiz.question_count} questions
                  </span>
                  {quiz.time_limit_minutes > 0 && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {quiz.time_limit_minutes} min
                    </span>
                  )}
                  <span>{quiz.passing_score}% to pass</span>
                  {quiz.required_belt && (
                    <BeltIndicator belt={quiz.required_belt} size="sm" />
                  )}
                </div>

                {quiz.attempt_count > 0 && (
                  <div className="mt-2 text-sm">
                    {quiz.passed ? (
                      <span className="text-green-600 font-medium">
                        Passed! Best: {quiz.best_score?.toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-orange-600">
                        Best attempt: {quiz.best_score?.toFixed(0)}%
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col items-end gap-2">
                {!quiz.is_accessible ? (
                  <div className="flex items-center gap-1 text-gray-400">
                    <Lock className="h-4 w-4" />
                    <BeltIndicator belt={quiz.required_belt} size="sm" />
                  </div>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="memberPrimary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectQuiz(quiz);
                      }}
                    >
                      {quiz.attempt_count > 0 ? (quiz.passed ? 'Retake' : 'Try Again') : 'Start'}
                    </Button>

                    {/* Materials Button - only show if quiz has sources */}
                    {quiz.sources_html && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMaterialsQuiz(quiz);
                        }}
                      >
                        <BookOpen className="h-3.5 w-3.5 mr-1" />
                        Materials
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      </div>
    </>
  );
};

// ============================================
// Main Quiz Section Component
// ============================================
const QuizSection = ({ studentId, token }) => {
  const { getBeltBySlug } = useBelts();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [points, setPoints] = useState(null);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchQuizzes = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/quizzes`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setQuizzes(data);
      }
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    } finally {
      setLoading(false);
    }
  }, [studentId, getAuthHeaders]);

  const fetchPoints = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${studentId}/points`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setPoints(data);
      }
    } catch (error) {
      console.error('Failed to fetch points:', error);
    }
  }, [studentId, getAuthHeaders]);

  useEffect(() => {
    fetchQuizzes();
    fetchPoints();
  }, [fetchQuizzes, fetchPoints]);

  const handleQuizComplete = (result) => {
    setQuizResult(result);
    fetchQuizzes(); // Refresh quiz list with updated scores
    fetchPoints(); // Refresh streak data
  };

  const handleRetry = () => {
    setQuizResult(null);
    // Quiz player will restart
  };

  const handleBack = () => {
    setSelectedQuiz(null);
    setQuizResult(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Show result screen
  if (quizResult && selectedQuiz) {
    return (
      <QuizResult
        result={quizResult}
        quiz={selectedQuiz}
        onRetry={handleRetry}
        onBack={handleBack}
      />
    );
  }

  // Show quiz player
  if (selectedQuiz) {
    return (
      <QuizPlayer
        quiz={selectedQuiz}
        studentId={studentId}
        token={token}
        onComplete={handleQuizComplete}
        onBack={handleBack}
      />
    );
  }

  // Show quiz list
  if (quizzes.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <FileQuestion className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium mb-2">No Quizzes Available</h3>
          <p className="text-gray-600 dark:text-gray-400">
            Check back later for new quizzes to test your knowledge.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Sort quizzes by belt level (beginner to advanced)
  const sortedQuizzes = [...quizzes].sort((a, b) => {
    const beltA = a.required_belt ? getBeltBySlug(a.required_belt) : null;
    const beltB = b.required_belt ? getBeltBySlug(b.required_belt) : null;

    // No requirement comes first
    if (!beltA && beltB) return -1;
    if (beltA && !beltB) return 1;
    if (!beltA && !beltB) return 0;

    // Both have belts, compare by order
    return (beltA.order || 0) - (beltB.order || 0);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FileQuestion className="h-5 w-5 text-blue-600" />
          Quizzes
        </h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {sortedQuizzes.filter(q => q.passed).length} of {sortedQuizzes.filter(q => q.is_accessible).length} passed
        </span>
      </div>

      <QuizList
        quizzes={sortedQuizzes}
        onSelectQuiz={setSelectedQuiz}
      />

      {/* Quiz Progress Section */}
      {points && (
        <Card>
          <CardContent className="p-4">
            <h3 className="font-medium text-gray-900 dark:text-gray-50 mb-4 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Your Quiz Progress
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Quiz Streak */}
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                <Flame className={`h-6 w-6 mx-auto mb-1 ${points.current_quiz_streak > 0 ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`} />
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{points.current_quiz_streak || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Correct Streak</p>
                <p className="text-xs text-green-400">Best: {points.longest_quiz_streak || 0}</p>
              </div>

              {/* Quizzes Passed */}
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <CheckCircle className="h-6 w-6 mx-auto mb-1 text-blue-500" />
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{points.quizzes_passed || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Quizzes Passed</p>
              </div>

              {/* Perfect Scores */}
              <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                <Star className="h-6 w-6 mx-auto mb-1 text-purple-500" />
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{points.perfect_quizzes || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Perfect Scores</p>
              </div>

              {/* Quiz Points */}
              <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg">
                <Target className="h-6 w-6 mx-auto mb-1 text-yellow-500" />
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{points.quiz_points || 0}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Quiz Points</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuizSection;
